import type { createCaller } from "@dransay/api";
import {
	type DeliveryMethodKind,
	type ProductVendorListItem,
	VENDOR_AGGREGATION_GROUP,
	type VendorListItem,
} from "@dransay/api/models";
import type { TFunction } from "i18next";
import slugify from "slugify";
import { getAggregatedVendorOptions } from "~/features/products/vendor-options";

export type ErrorResponse = { error: Response };
export type RedirectResponse = { redirect: string; status?: 301 | 302 };
export type SuccessResponse = {
	success: true;
	vendor: VendorListItem;
	deliveryMethod: string;
};

export type VendorValidationResult =
	| ErrorResponse
	| RedirectResponse
	| SuccessResponse;

export interface BaseValidationContext {
	request: Request;
	vendorId: string;
	deliveryMethod: string;
	vendorName?: string;
	caller: ReturnType<typeof createCaller>;
	t: TFunction<"product">;
}

export const VENDOR_PARAMS = {
	vendorId: "vendorId",
	deliveryMethod: "deliveryMethod",
	postalCode: "postalCode",
};

type VendorToSlugify = Pick<VendorListItem, "id" | "name">;

export const getDefaultDeeplinkBaseUrl = (
	request: Request,
	protocol: "http" | "https",
) => {
	const requestUrl = new URL(request.url);
	const baseUrl = new URL(requestUrl.origin);

	baseUrl.protocol = protocol;

	return baseUrl.toString();
};

export function createVendorDeeplink(
	baseShopUrl: string,
	vendor: VendorToSlugify,
	deliveryMethod: DeliveryMethodKind,
) {
	const nameSlug = getVendorSlug(vendor);
	const url = new URL(baseShopUrl);

	url.searchParams.set(VENDOR_PARAMS.vendorId, vendor.id);
	url.searchParams.set(VENDOR_PARAMS.deliveryMethod, deliveryMethod);
	url.pathname = `/${nameSlug}/${vendor.id}/${deliveryMethod}`;

	return url.toString();
}

export function findVendorOption<T extends { items: VendorListItem[] }>(
	vendorId: string | null,
	deliveryMethod: DeliveryMethodKind,
	t: TFunction<"product">,
	vendorsData: T,
) {
	const aggregatedVendorOptions = getAggregatedVendorOptions(deliveryMethod, t);

	const vendor =
		aggregatedVendorOptions.find((option) => option.id === vendorId) ||
		vendorsData?.items[0];

	return { vendor, aggregatedVendorOptions };
}

export function getVendorSlug(vendor: VendorToSlugify) {
	return slugify(vendor.name ?? `pharmacy-${vendor.id}`, {
		lower: true,
		strict: true,
	});
}

export type VendorWithFeaturedFlag = {
	featured?: boolean;
	isTop?: boolean | null;
};

/**
 * `getVendors` returns the DB column `vendor.featured` as `isTop`,
 * while `getProductVendors` and `getNearbyVendors` return it as `featured`.
 * This helper normalizes both shapes to a single boolean.
 */
export function resolveVendorFeatured(vendor: VendorWithFeaturedFlag): boolean {
	if (vendor.featured != null) {
		return vendor.featured;
	}
	return Boolean(vendor.isTop);
}

export const getDefaultVendorId = (
	vendors: ProductVendorListItem[],
	selectedVendorId: string | undefined,
): string | undefined => {
	const isAllPharmacies =
		String(selectedVendorId) === VENDOR_AGGREGATION_GROUP.ALL;
	const hasTopPharmacies = vendors.some((v) => v.featured === true);

	const vendorMatching = vendors.reduce(
		(acc, v) => {
			if (isAllPharmacies || !hasTopPharmacies || v.featured === true) {
				if (v.price) {
					if (!acc.price || acc.price > v.price) {
						acc.defaultVendorId = [v.id.toString()];
						acc.price = v.price;
					} else if (acc.price === v.price) {
						acc.defaultVendorId = acc.defaultVendorId
							? [...acc.defaultVendorId, v.id.toString()]
							: [v.id.toString()];
					}
				} else if (!acc.price) {
					acc.defaultVendorId = acc.defaultVendorId
						? [...acc.defaultVendorId, v.id.toString()]
						: [v.id.toString()];
				}
			}

			if (!acc.exactMatch && v.id.toString() === selectedVendorId) {
				acc.exactMatch = v.id.toString();
			}

			return acc;
		},
		{ defaultVendorId: undefined, exactMatch: undefined } as {
			defaultVendorId?: string[];
			price?: number;
			exactMatch?: string;
		},
	);

	const randomIndex = vendorMatching.defaultVendorId
		? Math.floor(Math.random() * vendorMatching.defaultVendorId.length)
		: 0;

	return (
		vendorMatching.exactMatch ||
		vendorMatching.defaultVendorId?.[randomIndex] ||
		vendors[0]?.id.toString()
	);
};
